import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera, Upload, Sparkles, Loader2, Check, ArrowRight, ArrowLeft, ImageIcon, AlertTriangle, X,
  Wand2, Home, Bath, Sofa, Bed, Briefcase, MapPin, ShieldCheck,
  Utensils, Shirt, DoorOpen, ArrowUpDown, Dumbbell, Baby, Layers,
  Building2, Car, MapPinned, Palmtree, Trees, Droplets, Warehouse, Sun, Landmark, HardHat,
  Sparkle,
} from "lucide-react";
import { useApp, API } from "../AppContext";
import { toast } from "sonner";
import { compressImage } from "../lib/imageCompression";

const ROOM_ICONS = {
  // Interior
  kitchen: Home, bathroom: Bath, living_room: Sofa, bedroom: Bed, office: Briefcase,
  dining_room: Utensils, basement: Layers, laundry: Shirt, hallway: DoorOpen,
  walk_in_closet: Shirt, stairway: ArrowUpDown, home_gym: Dumbbell, playroom: Baby, attic: Layers,
  // Exterior
  facade: Building2, garage: Car, driveway: MapPinned, patio_deck: Sun,
  backyard: Trees, front_yard: Palmtree, pool_area: Droplets, shed: Warehouse,
  balcony: Sun, porch: Landmark, roof: HardHat,
  // Ensemble
  full_interior: Home, full_exterior: Building2, whole_house: Sparkle, commercial_space: Building2,
  other: Home,
};

const ROOM_GROUPS_FR = [
  { key: "interior", label: "Intérieur", icon: Home, items: [
    { id: "kitchen", label: "Cuisine" },
    { id: "bathroom", label: "Salle de bain" },
    { id: "living_room", label: "Salon" },
    { id: "bedroom", label: "Chambre" },
    { id: "dining_room", label: "Salle à manger" },
    { id: "office", label: "Bureau" },
    { id: "basement", label: "Sous-sol" },
    { id: "laundry", label: "Salle de lavage" },
    { id: "hallway", label: "Corridor / Entrée" },
    { id: "walk_in_closet", label: "Walk-in / Garde-robe" },
    { id: "stairway", label: "Cage d'escalier" },
    { id: "home_gym", label: "Salle d'entraînement" },
    { id: "playroom", label: "Salle de jeux" },
    { id: "attic", label: "Grenier" },
  ]},
  { key: "exterior", label: "Extérieur", icon: Building2, items: [
    { id: "facade", label: "Façade extérieure" },
    { id: "garage", label: "Garage" },
    { id: "driveway", label: "Entrée de voiture" },
    { id: "patio_deck", label: "Patio / Terrasse" },
    { id: "backyard", label: "Cour arrière" },
    { id: "front_yard", label: "Cour avant" },
    { id: "pool_area", label: "Aménagement piscine" },
    { id: "shed", label: "Cabanon / Remise" },
    { id: "balcony", label: "Balcon" },
    { id: "porch", label: "Galerie / Véranda" },
    { id: "roof", label: "Toiture" },
  ]},
  { key: "whole", label: "Ensemble", icon: Sparkle, items: [
    { id: "full_interior", label: "Intérieur complet" },
    { id: "full_exterior", label: "Extérieur complet" },
    { id: "whole_house", label: "Maison complète" },
    { id: "commercial_space", label: "Espace commercial" },
    { id: "other", label: "Autre (préciser ci-dessous)" },
  ]},
];

const ROOM_GROUPS_EN = [
  { key: "interior", label: "Interior", icon: Home, items: [
    { id: "kitchen", label: "Kitchen" },
    { id: "bathroom", label: "Bathroom" },
    { id: "living_room", label: "Living room" },
    { id: "bedroom", label: "Bedroom" },
    { id: "dining_room", label: "Dining room" },
    { id: "office", label: "Office" },
    { id: "basement", label: "Basement" },
    { id: "laundry", label: "Laundry room" },
    { id: "hallway", label: "Hallway / Foyer" },
    { id: "walk_in_closet", label: "Walk-in closet" },
    { id: "stairway", label: "Stairway" },
    { id: "home_gym", label: "Home gym" },
    { id: "playroom", label: "Playroom" },
    { id: "attic", label: "Attic" },
  ]},
  { key: "exterior", label: "Exterior", icon: Building2, items: [
    { id: "facade", label: "Facade" },
    { id: "garage", label: "Garage" },
    { id: "driveway", label: "Driveway" },
    { id: "patio_deck", label: "Patio / Deck" },
    { id: "backyard", label: "Backyard" },
    { id: "front_yard", label: "Front yard" },
    { id: "pool_area", label: "Pool area" },
    { id: "shed", label: "Shed" },
    { id: "balcony", label: "Balcony" },
    { id: "porch", label: "Porch" },
    { id: "roof", label: "Roof" },
  ]},
  { key: "whole", label: "Whole", icon: Sparkle, items: [
    { id: "full_interior", label: "Full interior" },
    { id: "full_exterior", label: "Full exterior" },
    { id: "whole_house", label: "Whole house" },
    { id: "commercial_space", label: "Commercial space" },
    { id: "other", label: "Other (describe below)" },
  ]},
];

const STYLES = [
  { id: "modern", labelFr: "Moderne", labelEn: "Modern" },
  { id: "classic", labelFr: "Classique", labelEn: "Classic" },
  { id: "luxury", labelFr: "Luxe", labelEn: "Luxury" },
];

const Step = ({ n, active, done, label }) => (
  <div className="flex items-center gap-2" data-testid={`vision-step-${n}`}>
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
      style={{
        background: done ? "var(--pg-success)" : active ? "var(--pg-gold)" : "var(--pg-bg-tertiary)",
        color: done || active ? "var(--pg-text-inverse)" : "var(--pg-text-muted)",
        border: `1px solid ${done ? "var(--pg-success)" : active ? "var(--pg-gold)" : "var(--pg-border)"}`,
      }}
    >
      {done ? <Check size={14} /> : n}
    </div>
    <span className="text-xs font-semibold hidden sm:inline" style={{ color: active || done ? "var(--pg-text-primary)" : "var(--pg-text-muted)" }}>{label}</span>
  </div>
);

export default function VisionQuoteWizard() {
  const { lang, token } = useApp();
  const isFr = lang === "fr";
  const nav = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [roomType, setRoomType] = useState("kitchen");
  const [roomGroup, setRoomGroup] = useState("interior"); // interior | exterior | whole
  const [customDescription, setCustomDescription] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]); // up to 3 extra photos (base64)

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sessionId, setSessionId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [variants, setVariants] = useState([]);
  const [chosenVariantId, setChosenVariantId] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [createdQuote, setCreatedQuote] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");

  // Catalog pricing options (Québec/Canada 2026)
  const [catalog, setCatalog] = useState(null);
  const [region, setRegion] = useState("montreal");
  const [useCcqRates, setUseCcqRates] = useState(true);
  const [useCatalog, setUseCatalog] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vision/pricing-catalog`).then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const T = isFr ? {
    title: "PROGÈRE Vision",
    subtitle: "Photo → 3 rendus IA → Estimation → Devis brouillon",
    steps: ["Photo", "Analyse", "Choix du style", "Devis"],
    roomTitle: "Quelle pièce voulez-vous rénover ?",
    uploadHint: "Prenez ou choisissez une photo claire de la pièce actuelle.",
    takePhoto: "Prendre une photo",
    choosePhoto: "Choisir un fichier",
    analyzing: "Analyse de la photo par PROGÈRE AI…",
    generating: "Génération des rendus (30-60 secondes)…",
    estimating: "Calcul des travaux et coûts…",
    creating: "Création du devis brouillon…",
    next: "Continuer",
    back: "Retour",
    startAnalysis: "Lancer l'analyse IA",
    chooseStyle: "Choisir ce style",
    aiDisclaimer: "Contenu préparé avec PROGÈRE AI — validation humaine requise avant l'envoi au client.",
    estimateReady: "Estimation prête",
    saveQuote: "Enregistrer comme devis brouillon",
    quoteCreated: "Devis créé avec succès",
    viewQuote: "Ouvrir le devis",
    exclusions: "Exclusions",
    notes: "Notes",
    totalHt: "Sous-total",
    total: "Total (incl. taxes)",
    clientOptional: "Client (facultatif)",
    genericError: "Une erreur est survenue. Réessayez.",
  } : {
    title: "PROGÈRE Vision",
    subtitle: "Photo → 3 AI renders → Estimate → Draft quote",
    steps: ["Photo", "Analysis", "Style choice", "Quote"],
    roomTitle: "Which room do you want to renovate?",
    uploadHint: "Take or choose a clear photo of the current room.",
    takePhoto: "Take photo",
    choosePhoto: "Choose file",
    analyzing: "Analyzing photo with PROGÈRE AI…",
    generating: "Generating renders (30-60 seconds)…",
    estimating: "Calculating work and costs…",
    creating: "Creating draft quote…",
    next: "Continue",
    back: "Back",
    startAnalysis: "Start AI analysis",
    chooseStyle: "Choose this style",
    aiDisclaimer: "Content prepared with PROGÈRE AI — human validation required before sending to the client.",
    estimateReady: "Estimate ready",
    saveQuote: "Save as draft quote",
    quoteCreated: "Quote created successfully",
    viewQuote: "Open quote",
    exclusions: "Exclusions",
    notes: "Notes",
    totalHt: "Subtotal",
    total: "Total (incl. taxes)",
    clientOptional: "Client (optional)",
    genericError: "An error occurred. Try again.",
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error(isFr ? "Image trop grande (max 20 MB)" : "Image too large (max 20 MB)");
      return;
    }
    try {
      // Compress client-side to stay under Cloudflare 100s / large payload limits
      const { dataUrl, sizeKb, width, height } = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      setImageB64(dataUrl);
      setImagePreview(dataUrl);
      // Silent debug — helps diagnose if compression is doing its job
      console.debug(`[vision] compressed main photo → ${sizeKb} KB (${width}×${height})`);
    } catch (err) {
      console.warn("Compression failed, using original:", err);
      const reader = new FileReader();
      reader.onload = (e) => { setImageB64(e.target.result); setImagePreview(e.target.result); };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 3 - additionalImages.length);
    for (const f of arr) {
      if (f.size > 20 * 1024 * 1024) {
        toast.error(isFr ? `${f.name} trop grande (max 20 MB)` : `${f.name} too large (max 20 MB)`);
        continue;
      }
      try {
        const { dataUrl, sizeKb } = await compressImage(f, { maxDim: 1400, quality: 0.82 });
        console.debug(`[vision] compressed extra photo → ${sizeKb} KB`);
        setAdditionalImages((prev) => [...prev, dataUrl].slice(0, 3));
      } catch (err) {
        console.warn("Compression failed for", f.name, err);
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = (e) => res(e.target.result);
          r.readAsDataURL(f);
        });
        setAdditionalImages((prev) => [...prev, b64].slice(0, 3));
      }
    }
  };
  const removeAdditional = (idx) => setAdditionalImages((prev) => prev.filter((_, i) => i !== idx));

  // Progress bar state — client-side realistic simulation across the 3 AI phases
  const [progress, setProgress] = useState(0);          // 0..100
  const [progressLabel, setProgressLabel] = useState("");
  const [progressPhase, setProgressPhase] = useState(""); // "analyze" | "variants" | "estimate"

  // Animate a progress bar toward a target with easing (client-side, no backend needed)
  const runProgress = (phase, from, to, durationMs, label) => {
    setProgressPhase(phase);
    setProgressLabel(label);
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const raw = Math.min(1, elapsed / durationMs);
      // Ease-out (slows down asymptotically like real AI work)
      const eased = 1 - Math.pow(1 - raw, 2.2);
      const value = from + (to - from) * eased;
      setProgress(value);
      if (raw < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const runAnalyze = async () => {
    if (!imageB64) return;
    setLoading(true); setError(null);
    setProgress(0);
    runProgress("analyze", 0, 30, 15000, isFr ? "🔍 Analyse de la photo par l'IA…" : "🔍 AI is analyzing your photo…");
    try {
      const { data } = await axios.post(`${API}/vision/analyze`,
        {
          image_base64: imageB64,
          additional_images: additionalImages,
          room_type: roomType,
          custom_description: customDescription,
          lang: lang,
        }, authHeaders);
      setSessionId(data.session_id);
      setAnalysis(data.analysis);
      setStep(2);
      // Phase 2 — variants (3 rendus en parallèle)
      runProgress("variants", 30, 85, 25000, isFr ? "🎨 Génération de 3 rendus IA…" : "🎨 Generating 3 AI renders…");
      const { data: vd } = await axios.post(`${API}/vision/generate-variants`,
        { session_id: data.session_id, styles: STYLES.map((s) => s.id) }, authHeaders);
      setVariants(vd.variants_full || vd.variants || []);
      setProgress(100);
      setProgressLabel(isFr ? "✓ Rendus prêts — choisissez un style" : "✓ Renders ready — choose a style");
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || T.genericError);
      setProgress(0);
    } finally { setLoading(false); }
  };

  const chooseVariant = async (variantId) => {
    setChosenVariantId(variantId);
    setLoading(true); setError(null);
    setProgress(0);
    runProgress("estimate", 0, 92, 20000, isFr ? "💰 Estimation des coûts (catalogue Québec 2026)…" : "💰 Estimating costs (Quebec 2026 catalog)…");
    try {
      const { data } = await axios.post(`${API}/vision/estimate`,
        {
          session_id: sessionId, chosen_variant_id: variantId,
          region, use_ccq_rates: useCcqRates, use_catalog: useCatalog,
        }, authHeaders);
      setEstimate(data.estimate);
      setProgress(100);
      setProgressLabel(isFr ? "✓ Soumission prête" : "✓ Estimate ready");
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || T.genericError);
      setProgress(0);
    } finally { setLoading(false); }
  };

  const saveQuote = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/vision/create-quote`,
        { session_id: sessionId, client_name: clientName, client_email: clientEmail }, authHeaders);
      setCreatedQuote(data);
      toast.success(T.quoteCreated);
    } catch (e) {
      setError(e?.response?.data?.detail || T.genericError);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="page-vision-wizard">
      {/* Header */}
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
          <Sparkles size={11} className="inline mr-1" /> {T.title}
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black mt-1" style={{ color: "var(--pg-text-primary)" }}>
          <span style={{ color: "var(--pg-gold)" }}>PRO</span><span style={{ color: "var(--pg-red)" }}>GÈRE</span>{" "}
          <span>{isFr ? "VISION" : "VISION"}</span>
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--pg-text-secondary)" }}>{T.subtitle}</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto pb-2">
        {T.steps.map((label, i) => (
          <React.Fragment key={i}>
            <Step n={i + 1} active={step === i + 1} done={step > i + 1} label={label} />
            {i < T.steps.length - 1 && <div className="h-px w-6 sm:w-12 flex-shrink-0" style={{ background: "var(--pg-border)" }} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="rounded-xl p-4 flex items-start gap-2" data-testid="vision-error"
          style={{ background: "color-mix(in srgb, var(--pg-danger) 10%, transparent)", border: "1px solid var(--pg-danger)", color: "var(--pg-text-primary)" }}>
          <AlertTriangle size={16} style={{ color: "var(--pg-danger)" }} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* STEP 1 : PHOTO */}
      {step === 1 && (
        <div className="rounded-2xl p-6 sm:p-8" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
          <h2 className="font-heading text-xl font-bold mb-4" style={{ color: "var(--pg-text-primary)" }}>{T.roomTitle}</h2>

          {/* Group tabs (Intérieur / Extérieur / Ensemble) */}
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="vision-room-group-tabs">
            {(isFr ? ROOM_GROUPS_FR : ROOM_GROUPS_EN).map((g) => {
              const GroupIcon = g.icon;
              const active = roomGroup === g.key;
              return (
                <button key={g.key} data-testid={`vision-group-${g.key}`}
                  onClick={() => {
                    setRoomGroup(g.key);
                    // Auto-select first item of group
                    setRoomType(g.items[0].id);
                  }}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition"
                  style={{
                    background: active ? "linear-gradient(135deg, var(--pg-gold), #B8941F)" : "var(--pg-bg-tertiary)",
                    color: active ? "var(--pg-text-inverse)" : "var(--pg-text-secondary)",
                    border: `1px solid ${active ? "var(--pg-gold)" : "var(--pg-border)"}`,
                  }}>
                  <GroupIcon size={14} />
                  <span>{g.label}</span>
                </button>
              );
            })}
          </div>

          {/* Grille des espaces du groupe actif */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
            {((isFr ? ROOM_GROUPS_FR : ROOM_GROUPS_EN).find(g => g.key === roomGroup)?.items || []).map((it) => {
              const Icon = ROOM_ICONS[it.id] || Home;
              const active = roomType === it.id;
              return (
                <button key={it.id} data-testid={`vision-room-${it.id}`} onClick={() => setRoomType(it.id)}
                  className="rounded-xl p-3 flex flex-col items-center gap-2 transition"
                  style={{
                    background: active ? "color-mix(in srgb, var(--pg-gold) 15%, transparent)" : "var(--pg-bg-tertiary)",
                    border: `1px solid ${active ? "var(--pg-gold)" : "var(--pg-border)"}`,
                    color: active ? "var(--pg-gold)" : "var(--pg-text-secondary)",
                  }}>
                  <Icon size={20} />
                  <span className="text-[11px] font-semibold text-center leading-tight">{it.label}</span>
                </button>
              );
            })}
          </div>

          {/* Custom description (mandatoire si "other", optionnel sinon) */}
          <div className="mb-6">
            <label className="text-[11px] font-black uppercase tracking-widest mb-2 block" style={{ color: "var(--pg-gold)" }}>
              {isFr ? "Précisez ce que vous voulez rénover" : "Describe what you want to renovate"}
              <span className="ml-2 text-[10px]" style={{ color: "var(--pg-text-muted)" }}>
                ({roomType === "other" ? (isFr ? "requis" : "required") : (isFr ? "facultatif · recommandé" : "optional · recommended")})
              </span>
            </label>
            <textarea data-testid="vision-custom-description"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder={isFr
                ? "Ex: Refaire la façade en revêtement de fibrociment couleur charbon, changer les fenêtres avant, remplacer les gouttières et la porte d'entrée."
                : "E.g., Redo the facade with charcoal fiber cement siding, replace front windows, gutters, and the entry door."}
              rows={3}
              maxLength={600}
              className="input-dark w-full text-sm"
              style={{ background: "var(--pg-bg-tertiary)", color: "var(--pg-text-primary)", border: "1px solid var(--pg-border)", borderRadius: "12px", padding: "12px 14px", resize: "vertical" }} />
            <div className="mt-1 text-[10px] text-right" style={{ color: "var(--pg-text-muted)" }}>
              {customDescription.length}/600
            </div>
          </div>

          <p className="text-sm mb-4" style={{ color: "var(--pg-text-secondary)" }}>{T.uploadHint}</p>

          {imagePreview ? (
            <div className="mb-4">
              <img src={imagePreview} alt="preview" className="rounded-xl w-full max-h-80 object-cover" data-testid="vision-photo-preview" />
              <button onClick={() => { setImageB64(null); setImagePreview(null); }}
                className="mt-2 text-xs" style={{ color: "var(--pg-text-muted)" }}>
                {isFr ? "Changer la photo" : "Change photo"}
              </button>

              {/* Additional photos (up to 3) — for better analysis of exteriors / large spaces */}
              <div className="mt-4">
                <div className="text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: "var(--pg-gold)" }}>
                  {isFr ? `Photos additionnelles (${additionalImages.length}/3)` : `Additional photos (${additionalImages.length}/3)`}
                  <span className="ml-2 font-normal" style={{ color: "var(--pg-text-muted)" }}>
                    {isFr ? "· facultatif · améliore la précision (façade avant + côté + arrière)" : "· optional · improves accuracy"}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {additionalImages.map((src, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden group"
                      style={{ border: "1px solid var(--pg-border)" }} data-testid={`additional-photo-${i}`}>
                      <img src={src} alt={`extra ${i + 1}`} className="w-full h-full object-cover" />
                      <button onClick={() => removeAdditional(i)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: "rgba(11,12,15,0.85)", border: "1px solid var(--pg-red)" }}
                        data-testid={`additional-remove-${i}`}>
                        <X size={12} color="var(--pg-red)" />
                      </button>
                    </div>
                  ))}
                  {additionalImages.length < 3 && (
                    <label className="aspect-square rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer transition hover:brightness-110"
                      style={{ background: "var(--pg-bg-tertiary)", border: "2px dashed var(--pg-border-strong)", color: "var(--pg-text-muted)" }}
                      data-testid="additional-photo-add">
                      <input type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => handleAdditionalFiles(e.target.files)} />
                      <ImageIcon size={20} style={{ color: "var(--pg-gold)" }} />
                      <span className="text-[10px] font-semibold">{isFr ? "Ajouter" : "Add"}</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="vision-file-input" />
              <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.capture = "environment"; fileInputRef.current.click(); } }}
                data-testid="vision-take-photo-btn"
                className="rounded-xl p-8 flex flex-col items-center gap-2 transition hover:brightness-110"
                style={{ background: "var(--pg-bg-tertiary)", border: "2px dashed var(--pg-border-strong)", color: "var(--pg-text-primary)" }}>
                <Camera size={28} style={{ color: "var(--pg-gold)" }} />
                <span className="text-sm font-semibold">{T.takePhoto}</span>
              </button>
              <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.removeAttribute("capture"); fileInputRef.current.click(); } }}
                data-testid="vision-choose-photo-btn"
                className="rounded-xl p-8 flex flex-col items-center gap-2 transition hover:brightness-110"
                style={{ background: "var(--pg-bg-tertiary)", border: "2px dashed var(--pg-border-strong)", color: "var(--pg-text-primary)" }}>
                <Upload size={28} style={{ color: "var(--pg-gold)" }} />
                <span className="text-sm font-semibold">{T.choosePhoto}</span>
              </button>
            </div>
          )}

          <button disabled={!imageB64 || loading} onClick={runAnalyze} data-testid="vision-start-btn"
            className="w-full text-sm font-bold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
            {loading ? <><Loader2 size={14} className="animate-spin" /> {T.analyzing}</> : <><Wand2 size={14} /> {T.startAnalysis}</>}
          </button>
        </div>
      )}

      {/* STEP 2/3 : ANALYSE + CHOIX */}
      {(step === 2 || step === 3) && (
        <div className="rounded-2xl p-6" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
          {loading && (!variants.length || chosenVariantId) && (
            <div className="text-center py-14 rounded-2xl mb-6" data-testid="vision-generating"
              style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(192,0,0,0.04))", border: "1px solid rgba(212,175,55,0.35)" }}>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center relative"
                style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", boxShadow: "0 8px 24px rgba(212,175,55,0.4)" }}>
                <Sparkles size={28} color="#0B0C0F" strokeWidth={2.5} className="animate-pulse" />
              </div>
              <div className="text-sm font-bold mb-1" data-testid="progress-label" style={{ color: "var(--pg-text-primary)" }}>
                {progressLabel || T.generating}
              </div>
              <div className="text-[11px] mb-4" style={{ color: "var(--pg-text-muted)" }}>
                {isFr ? "Cela prend habituellement 25 à 50 secondes." : "This typically takes 25 to 50 seconds."}
              </div>
              {/* Progress bar */}
              <div className="max-w-md mx-auto px-6">
                <div className="h-2 rounded-full overflow-hidden relative" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded-full transition-all"
                    data-testid="progress-bar"
                    style={{
                      width: `${Math.round(progress)}%`,
                      background: "linear-gradient(90deg, #F5D062 0%, #D4AF37 50%, #C00000 100%)",
                      boxShadow: "0 0 12px rgba(212,175,55,0.5)",
                    }}>
                    <div className="absolute inset-0 opacity-40"
                      style={{
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                        animation: "shimmer 1.6s infinite",
                      }} />
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--pg-text-muted)" }}>
                  <span>
                    {progressPhase === "analyze" && (isFr ? "Phase 1/3 · Analyse" : "Phase 1/3 · Analysis")}
                    {progressPhase === "variants" && (isFr ? "Phase 2/3 · Rendus" : "Phase 2/3 · Renders")}
                    {progressPhase === "estimate" && (isFr ? "Phase 3/3 · Estimation" : "Phase 3/3 · Estimate")}
                  </span>
                  <span data-testid="progress-percent" style={{ color: "var(--pg-gold)" }}>{Math.round(progress)}%</span>
                </div>
              </div>
            </div>
          )}

          {variants.length > 0 && !(loading && chosenVariantId) && (
            <>
              <h2 className="font-heading text-xl font-bold mb-4" style={{ color: "var(--pg-text-primary)" }}>
                {isFr ? "Choisissez un style" : "Choose a style"}
              </h2>

              {/* Pricing catalog selector (Quebec/Canada 2026) */}
              {catalog && (
                <div className="rounded-xl p-4 mb-5" data-testid="pricing-catalog-selector"
                  style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(192,0,0,0.04))", border: "1px solid rgba(212,175,55,0.35)" }}>
                  <div className="flex items-start gap-3 mb-3">
                    <ShieldCheck size={18} style={{ color: "var(--pg-gold)" }} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-widest font-black" style={{ color: "var(--pg-gold)" }}>
                        {isFr ? `Prix ancrés · Catalogue Québec ${catalog.year}` : `Anchored pricing · Quebec ${catalog.year} catalog`}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--pg-text-muted)" }}>
                        {isFr
                          ? `${catalog.material_item_count} items · Taux CCQ + charges patronales · Ajustement régional`
                          : `${catalog.material_item_count} items · CCQ rates loaded · Regional adjustment`}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 mb-1.5" style={{ color: "var(--pg-text-secondary)" }}>
                        <MapPin size={11} /> {isFr ? "Région" : "Region"}
                      </label>
                      <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="region-select"
                        className="input-dark text-xs w-full"
                        style={{ background: "var(--pg-bg-tertiary)", color: "var(--pg-text-primary)", border: "1px solid var(--pg-border)", borderRadius: "8px", padding: "8px 12px" }}>
                        {catalog.regions.map((r) => (
                          <option key={r.code} value={r.code}>
                            {(isFr ? r.label_fr : (r.label_en || r.label_fr))} (×{r.factor.toFixed(2)})
                          </option>
                        ))}
                      </select>
                    </div>

                    <label className="flex items-start gap-2 rounded-lg p-3 cursor-pointer"
                      style={{ background: useCcqRates ? "rgba(212,175,55,0.10)" : "var(--pg-bg-tertiary)", border: `1px solid ${useCcqRates ? "rgba(212,175,55,0.4)" : "var(--pg-border)"}` }}>
                      <input type="checkbox" checked={useCcqRates} onChange={(e) => setUseCcqRates(e.target.checked)}
                        data-testid="use-ccq-toggle" className="mt-0.5" />
                      <div>
                        <div className="text-xs font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                          {isFr ? "Taux CCQ chargés" : "Loaded CCQ rates"}
                        </div>
                        <div className="text-[10px]" style={{ color: "var(--pg-text-muted)" }}>
                          {isFr ? "Avec charges patronales (~38 %)" : "With employer charges (~38%)"}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 rounded-lg p-3 cursor-pointer"
                      style={{ background: useCatalog ? "rgba(212,175,55,0.10)" : "var(--pg-bg-tertiary)", border: `1px solid ${useCatalog ? "rgba(212,175,55,0.4)" : "var(--pg-border)"}` }}>
                      <input type="checkbox" checked={useCatalog} onChange={(e) => setUseCatalog(e.target.checked)}
                        data-testid="use-catalog-toggle" className="mt-0.5" />
                      <div>
                        <div className="text-xs font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                          {isFr ? "Ancrer sur catalogue" : "Anchor on catalog"}
                        </div>
                        <div className="text-[10px]" style={{ color: "var(--pg-text-muted)" }}>
                          {isFr ? "Snap serveur des prix réels 2026" : "Server-side snap to 2026 pricing"}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {variants.map((v) => (
                  <button key={v.id} data-testid={`vision-variant-${v.style}`}
                    disabled={loading}
                    onClick={() => chooseVariant(v.id)}
                    className="rounded-xl overflow-hidden text-left transition hover:brightness-110 disabled:opacity-60"
                    style={{
                      background: "var(--pg-bg-tertiary)",
                      border: `2px solid ${chosenVariantId === v.id ? "var(--pg-gold)" : "var(--pg-border)"}`,
                    }}>
                    <img src={`data:image/png;base64,${v.image_b64}`} alt={v.label_fr}
                      className="w-full aspect-square object-cover" />
                    <div className="p-3">
                      <div className="font-semibold text-sm" style={{ color: "var(--pg-text-primary)" }}>
                        {isFr ? v.label_fr : (STYLES.find(s => s.id === v.style)?.labelEn || v.label_fr)}
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--pg-gold)" }}>
                        {chosenVariantId === v.id && loading ? T.estimating : T.chooseStyle}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {analysis && (
            <details className="mt-6 rounded-xl p-4" style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}>
              <summary className="text-xs font-semibold cursor-pointer" style={{ color: "var(--pg-text-secondary)" }}>
                {isFr ? "Analyse détaillée de la pièce" : "Detailed room analysis"}
              </summary>
              <pre className="mt-3 text-xs whitespace-pre-wrap" style={{ color: "var(--pg-text-muted)" }}>{JSON.stringify(analysis, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      {/* STEP 4 : DEVIS */}
      {step === 4 && estimate && (
        <div className="space-y-4">
          <div className="rounded-2xl p-6" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-success)" }}>
                  <Check size={11} className="inline" /> {T.estimateReady}
                </div>
                <h2 className="font-heading text-xl font-bold mt-1" style={{ color: "var(--pg-text-primary)" }}>{estimate.title}</h2>
              </div>
              <div className="text-right">
                <div className="text-xs" style={{ color: "var(--pg-text-muted)" }}>{T.total}</div>
                <div className="text-2xl font-black" style={{ color: "var(--pg-gold)" }}>
                  {new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(estimate.grand_total || 0)}
                </div>
              </div>
            </div>

            {/* Pricing traceability badge */}
            {estimate.pricing && estimate.pricing.source === "quebec_canada_2026_catalog" && (
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-4 text-[11px]"
                data-testid="pricing-anchor-badge"
                style={{ background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.35)", color: "var(--pg-success)" }}>
                <ShieldCheck size={12} />
                <span className="font-semibold">
                  {isFr
                    ? `Prix ancrés · ${estimate.pricing.region_label_fr} (×${estimate.pricing.region_multiplier}) · ${estimate.pricing.coverage?.coverage_pct || 0}% catalogue`
                    : `Anchored pricing · ${estimate.pricing.region_label_fr} (×${estimate.pricing.region_multiplier}) · ${estimate.pricing.coverage?.coverage_pct || 0}% catalog`}
                </span>
              </div>
            )}

            {/* Sections */}
            {(estimate.sections || []).map((sec, i) => (
              <div key={i} className="mt-4">
                <div className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: "var(--pg-gold)" }}>{sec.name}</div>
                <div className="space-y-1">
                  {(sec.items || []).map((it, j) => (
                    <div key={j} className="flex items-center justify-between text-sm py-1.5 border-b" style={{ borderColor: "var(--pg-border)" }}>
                      <div className="flex-1 pr-4" style={{ color: "var(--pg-text-secondary)" }}>
                        {it.description}
                        <span className="text-xs ml-2" style={{ color: "var(--pg-text-muted)" }}>
                          {it.quantity} {it.unit}
                        </span>
                      </div>
                      <div className="font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                        {new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(it.lineTotal || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-6 pt-4 border-t space-y-1 text-sm" style={{ borderColor: "var(--pg-border)" }}>
              <div className="flex justify-between" style={{ color: "var(--pg-text-secondary)" }}>
                <span>{T.totalHt}</span>
                <span>{new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(estimate.subtotal || 0)}</span>
              </div>
              <div className="flex justify-between text-xs" style={{ color: "var(--pg-text-muted)" }}>
                <span>TPS (5%)</span><span>{new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(estimate.gst || 0)}</span>
              </div>
              <div className="flex justify-between text-xs" style={{ color: "var(--pg-text-muted)" }}>
                <span>TVQ (9.975%)</span><span>{new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(estimate.qst || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-base pt-2" style={{ color: "var(--pg-gold)" }}>
                <span>{T.total}</span>
                <span>{new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(estimate.grand_total || 0)}</span>
              </div>
            </div>

            {(estimate.exclusions?.length > 0 || estimate.notes?.length > 0) && (
              <div className="mt-4 pt-4 border-t space-y-2 text-xs" style={{ borderColor: "var(--pg-border)" }}>
                {estimate.exclusions?.length > 0 && (
                  <div><span className="font-semibold" style={{ color: "var(--pg-text-primary)" }}>{T.exclusions} :</span> <span style={{ color: "var(--pg-text-muted)" }}>{estimate.exclusions.join(" · ")}</span></div>
                )}
                {estimate.notes?.length > 0 && (
                  <div><span className="font-semibold" style={{ color: "var(--pg-text-primary)" }}>{T.notes} :</span> <span style={{ color: "var(--pg-text-muted)" }}>{estimate.notes.join(" · ")}</span></div>
                )}
              </div>
            )}
          </div>

          {/* AI disclaimer */}
          <div className="rounded-xl p-4 flex items-start gap-2" data-testid="vision-ai-disclaimer"
            style={{ background: "color-mix(in srgb, var(--pg-warning) 10%, transparent)", border: "1px solid var(--pg-warning)", color: "var(--pg-text-primary)" }}>
            <AlertTriangle size={16} style={{ color: "var(--pg-warning)", flexShrink: 0 }} />
            <span className="text-xs">{T.aiDisclaimer}</span>
          </div>

          {/* Client optional */}
          {!createdQuote && (
            <div className="rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1" style={{ color: "var(--pg-text-muted)" }}>{T.clientOptional}</label>
                <input value={clientName} onChange={(e) => setClientName(e.target.value)}
                  data-testid="vision-client-name"
                  className="progere-input w-full" placeholder={isFr ? "Nom du client" : "Client name"} />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1" style={{ color: "var(--pg-text-muted)" }}>Courriel</label>
                <input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
                  data-testid="vision-client-email"
                  className="progere-input w-full" placeholder="client@example.com" />
              </div>
            </div>
          )}

          {createdQuote ? (
            <div className="rounded-2xl p-6 text-center" style={{ background: "color-mix(in srgb, var(--pg-success) 10%, transparent)", border: "1px solid var(--pg-success)" }} data-testid="vision-quote-created">
              <Check size={32} className="mx-auto mb-3" style={{ color: "var(--pg-success)" }} />
              <div className="font-heading text-xl font-bold" style={{ color: "var(--pg-text-primary)" }}>{T.quoteCreated}</div>
              <div className="text-sm mt-1" style={{ color: "var(--pg-text-secondary)" }}>
                #{createdQuote.quote_number}
              </div>
              <button onClick={() => nav("/app/quotes")}
                data-testid="vision-view-quote-btn"
                className="mt-4 text-sm font-bold px-6 py-2.5 rounded-full"
                style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                {T.viewQuote} <ArrowRight size={12} className="inline ml-1" />
              </button>
            </div>
          ) : (
            <button onClick={saveQuote} disabled={loading}
              data-testid="vision-save-quote-btn"
              className="w-full text-sm font-bold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
              style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
              {loading ? <><Loader2 size={14} className="animate-spin" /> {T.creating}</> : <>{T.saveQuote} <ArrowRight size={14} /></>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
